import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import * as React from "react";
import { Link } from "react-router-dom";
import Icon from "../../../components/atoms/Icon";
import Loader from "../../../components/atoms/Loader";
import { FilterWrapper } from "../../../components/features/FilterWrapper";
import { TagFilter } from "../../../components/features/NewStudentFilter";
import { useStudentFilterContext } from "../../../components/features/NewStudentFilter/useStudentFilterContext";
import { Text } from "../../../components/general/Text";
import TabsNavi from "../../../components/molecules/TabsNavi";
import { useQueryError } from "../../../hooks/http/useQueryError";
import { useOnScrollContentBottom } from "../../../hooks/useOnScrollContentBottom";
import SectionInterface from "../../../interfaces/SectionInterface";
import StudentFollowSummaryCard from "./StudentFollowSummaryCard";
import {
  buildStudentFollowSummaryFilterParams,
  useFetchStudentFollowSummaries,
} from "./useFetchStudentFollowSummaries";

interface Props {
  section: SectionInterface;
}

const SectionsStudentFollowSummaries = ({ section }: Props) => {
  // タグ絞り込みは既存の生徒一覧のタグフィルタと同一方式（LIST-05）。
  // 選択状態は既存コンテキスト共用により校舎単位で永続化され、生徒一覧等の既存画面と共有される
  const { findTagFilter, isInitialized } = useStudentFilterContext();
  const studentFilter = findTagFilter({ sectionId: section.id });
  const filterParams = React.useMemo(
    () => buildStudentFollowSummaryFilterParams(studentFilter),
    [studentFilter],
  );
  // 空状態文言の出し分けに使う（絞り込み 0 件と全体 0 件を区別する。ai_question FILTER-04 と同じ前例）
  const isFiltering = studentFilter.selectedTags.length > 0;

  const {
    summaries,
    lastGeneratedAt,
    loadedPageCount,
    // isLoading ではなく isPending を使う: enabled 待ち（フィルタ復元中）も
    // ローディング表示にして、空状態文言が一瞬表示されるのを防ぐ
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    isFetchNextPageError,
  } = useFetchStudentFollowSummaries({
    sectionId: section.id,
    filterParams,
    // フィルタ選択状態の復元完了を待ってから取得する（復元前後の二重取得を防ぐ）
    enabled: isInitialized,
  });
  const hasSummaries = summaries !== undefined && summaries.length > 0;
  // 初回取得の失敗は共通処理（401/403 リダイレクト等）に委ねる。追加読み込みの失敗は
  // 取得済みサマリーの表示を維持するため抑制し、再試行の停止は下記の onScrollContentBottom で行う
  useQueryError(isFetchNextPageError ? null : error);

  // 次ページの取得は、リスト末尾の番兵が画面内に入ったときに行う（IntersectionObserver による
  // viewport 充填）。この画面は専用のスクロールコンテナを持たず MainLayout 側でスクロールする
  // ため、監視の基準は viewport（root: null）とする
  const { refLastElement } = useOnScrollContentBottom({
    rootContentWithScroll: null,
    onScrollContentBottom: () => {
      // 追加読み込みが失敗した後は再試行しない（リロードで復帰する）
      if (isPending || isFetchingNextPage || isFetchNextPageError) {
        return;
      }
      fetchNextPage();
    },
  });

  return (
    <div>
      <TabsNavi
        tabs={[
          {
            label: "AIアシスタントサマリー",
            link: "#",
            active: true,
          },
        ]}
      />
      <div className="mt-7 flex items-start justify-between gap-7">
        <div>
          {/* 連携状態（status）フィルタは置かない（LIST-05: 絞り込みは 3 種のタグのみ） */}
          <FilterWrapper
            StudentTagFilter={<TagFilter sectionId={section.id} />}
          />
          {/* 最終更新の案内。文言は Notion 仕様書（02. AIアシスタントサマリー一覧）準拠。
              日時はサーバー導出の meta.lastGeneratedAt（可視範囲内の参照対象サマリー全体の
              最新の生成完了日時。絞り込み条件に依存しない）。null（参照対象 0 件）の場合は
              日時を出せないため注記のみ表示する */}
          <p className="mt-6 text-lg text-black">
            {lastGeneratedAt
              ? `最終更新：${format(
                  parseISO(lastGeneratedAt),
                  "yyyy/MM/dd HH:mm",
                )}（※毎週月曜日に自動更新されます）`
              : "※毎週月曜日に自動更新されます"}
          </p>
        </div>
        <Link
          to={`/sections/${section.id}/student_follow_summaries/prompt`}
          className="buiButtonBase buiButtonVariantOutline shrink-0"
        >
          <Icon name="icon-setting" />
          プロンプト設定
        </Link>
      </div>

      {isPending ? (
        <Loader loading />
      ) : (
        <>
          {hasSummaries ? (
            <div className="mt-4 flex flex-col gap-7">
              {summaries?.map((summary) => (
                <StudentFollowSummaryCard
                  // student.id × startDate は boron 側の一意制約により一意（描画キー用途のみ・LIST-03）
                  key={`${summary.student.id}-${summary.startDate}`}
                  summary={summary}
                />
              ))}
            </div>
          ) : hasNextPage || isFetchingNextPage ? (
            // サマリーは空だが次のページを取得中
            <Loader loading />
          ) : (
            // 全ページを取得し切っても空のとき（LIST-04）。
            // タグ絞り込み中の 0 件は全体 0 件と文言を区別する（暫定文言。デザイン確定後に差し替え）。
            // 初回取得エラー時は useQueryError の共通処理に委ね、空状態と誤認させる文言は出さない
            !error && (
              <Text align="center" className="mt-8">
                {isFiltering
                  ? "絞り込み条件に一致するサマリーはありません。"
                  : "AIアシスタントサマリーはまだありません。"}
              </Text>
            )
          )}

          {hasSummaries && isFetchingNextPage && <Loader loading />}

          {/* 追加読み込みの失敗は取得済みの表示を維持したまま下部で提示する（不正カーソル 400 を含む。
              再試行は行わず、リロードでの復帰を促す） */}
          {isFetchNextPageError && (
            <Text align="center" className="mt-4">
              サマリーを追加で読み込めませんでした。ページを再読み込みしてください。
            </Text>
          )}

          {/* リスト末尾の番兵: 画面内に入ったら次ページを取得する。key の意図は loadedPageCount を参照 */}
          {hasNextPage && (
            <div
              key={loadedPageCount}
              ref={refLastElement}
              data-testid="student-follow-summaries-load-sentinel"
            />
          )}
        </>
      )}
    </div>
  );
};

export default SectionsStudentFollowSummaries;
